import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Payments from './pages/Payments';
import Penalties from './pages/Penalties';
import Expenses from './pages/Expenses';
import Loans from './pages/Loans';
import Members from './pages/Members';
import Meetings from './pages/Meetings';
import Rules from './pages/Rules';
import AuditLogs from './pages/AuditLogs';
import AdminPanel from './pages/AdminPanel';
import Profile from './pages/Profile';
import Distribution from './pages/Distribution';
import NotificationSettings from './pages/NotificationSettings';
import Layout from './components/Layout';

import { TOKENS, BRAND } from './theme/palette';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import { setAccentMode } from './theme/accents';

// Same cut-off as MUI's `down('sm')`, usable before a theme exists
const PHONE = '@media (max-width:599.95px)';

// One theme definition, two grounds. Light is the default; the tokens carry the
// warm ledger character across both so the app does not become generic on paper.
const buildTheme = (mode) => {
  const t = TOKENS[mode];
  const brand = BRAND[mode];

  return createTheme({
    palette: {
      mode,
      primary: { main: brand.primary, light: brand.primaryLight, dark: brand.primaryDark, contrastText: brand.onPrimary },
      secondary: { main: brand.secondary, contrastText: mode === 'light' ? '#FFFFFF' : '#1A1410' },
      background: { default: t.ground, paper: t.surface },
      success: { main: mode === 'light' ? '#1F8B5B' : '#6FA88A' },
      error: { main: mode === 'light' ? '#C03A18' : '#C4553B' },
      warning: { main: mode === 'light' ? '#C77A15' : '#D98F40' },
      info: { main: brand.secondary },
      divider: t.line,
      text: { primary: t.textHi, secondary: t.textLo },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      // Fraunces carries the headings - an heirloom-ledger voice rather than
      // another grotesk. Money figures use it too, at tabular widths.
      h1: { fontFamily: '"Fraunces", Georgia, serif', fontSize: '2.75rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h2: { fontFamily: '"Fraunces", Georgia, serif', fontSize: '2.125rem', fontWeight: 600, letterSpacing: '-0.02em' },
      h3: { fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h4: { fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '0.01em' },
      subtitle2: { fontWeight: 600, letterSpacing: '0.02em' },
      overline: {
        fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: t.textLo,
      },
      button: { fontWeight: 600, letterSpacing: '0.01em' },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Rupee columns only read as columns when digits are the same width
          'td, th, .tabular': { fontVariantNumeric: 'tabular-nums' },
          body: { backgroundColor: t.ground },
          '*:focus-visible': { outline: `2px solid ${brand.primary}`, outlineOffset: '2px' },
          '::selection': { background: mode === 'light' ? 'rgba(169,118,28,0.22)' : 'rgba(217,164,65,0.3)' },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-track': { background: t.ground },
          '::-webkit-scrollbar-thumb': { background: t.line, borderRadius: 999 },
          '@media (prefers-reduced-motion: reduce)': {
            '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
          },
          // Small icon buttons are ~28px, too tight for a thumb - and they sit
          // next to destructive actions (delete member, delete payment).
          '@media (pointer: coarse), (max-width: 599.95px)': {
            // Doubled class: MUI's own size rule has the same specificity and loads later
            '.MuiIconButton-root.MuiIconButton-sizeSmall': { padding: 10 },
            // Tappable chips (row "View" pills, filter chips) are 22px tall
            '.MuiChip-root.MuiChip-clickable.MuiChip-sizeSmall': { height: 32 },
          },
        },
      },
      // Phones: forms take the whole screen (a floating card leaves ~300px to
      // work in) while xs confirm dialogs stay compact and centred.
      MuiDialog: {
        styleOverrides: {
          paper: {
            [PHONE]: {
              '&.MuiDialog-paperWidthSm, &.MuiDialog-paperWidthMd, &.MuiDialog-paperWidthLg, &.MuiDialog-paperWidthXl': {
                margin: 0,
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                maxHeight: 'none',
                borderRadius: 0,
              },
              '&.MuiDialog-paperWidthXs': {
                margin: 16,
                width: 'calc(100% - 32px)',
                maxWidth: 'calc(100% - 32px)',
                maxHeight: 'calc(100% - 32px)',
              },
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { [PHONE]: { padding: '16px 16px 8px' } } },
      },
      MuiDialogContent: {
        styleOverrides: { root: { [PHONE]: { padding: 16 } } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { [PHONE]: { padding: '12px 16px', gap: 8, flexWrap: 'wrap' } } },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 10, padding: '8px 18px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: {
            background: `linear-gradient(180deg, ${brand.primaryLight} 0%, ${brand.primary} 100%)`,
            color: brand.onPrimary,
            '&:hover': { background: `linear-gradient(180deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)` },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: t.surface,
            border: `1px solid ${t.line}`,
            transition: 'border-color 160ms ease, transform 160ms ease',
            ...(mode === 'light' ? { boxShadow: '0 1px 2px rgba(36,28,20,0.05)' } : {}),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: t.line },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600, letterSpacing: '0.01em' }, sizeSmall: { height: 22 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.line },
          head: { color: t.textLo, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: mode === 'light' ? '#241C14' : t.surfaceHi, border: `1px solid ${t.line}`, fontSize: '0.75rem' },
        },
      },
    },
  });
};

const ThemedApp = () => {
  const { mode } = useThemeMode();
  const isPhone = useMediaQuery(PHONE, { noSsr: true });
  // Accents follow the ground, so chips and figures stay readable in both
  setAccentMode(mode);
  const theme = React.useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <NotificationProvider>
            <Router>
            <Toaster
              // top-right lands on the theme toggle, bell and avatar on a phone
              position={isPhone ? 'top-center' : 'top-right'}
              containerStyle={isPhone ? { top: 68 } : undefined}
              toastOptions={{
                duration: 4000,
                // Match the app surfaces rather than the library's default grey
                style: {
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '12px',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  boxShadow: mode === 'light' ? '0 8px 24px rgba(36,28,20,0.12)' : '0 12px 32px rgba(0,0,0,0.45)',
                },
                success: { iconTheme: { primary: theme.palette.success.main, secondary: theme.palette.background.paper } },
                error: { iconTheme: { primary: theme.palette.error.main, secondary: theme.palette.background.paper } },
              }}
            />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<PrivateRoute />}>
                <Route element={<Layout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/penalties" element={<Penalties />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/loans" element={<Loans />} />
                  <Route path="/members" element={<Members />} />
                  <Route path="/meetings" element={<Meetings />} />
                  <Route path="/distribution" element={<Distribution />} />
                  <Route path="/silver-distribution" element={<Navigate to="/distribution" replace />} />
                  <Route path="/rules" element={<Rules />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/notification-settings" element={<NotificationSettings />} />
                  <Route element={<AdminRoute />}>
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/audit" element={<AuditLogs />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
            </Router>
          </NotificationProvider>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;